'use client';

import ImageCard from '@dvd/components/ImageCard';
import LogoShowcase from '@dvd/components/LogoShowcase';
import Section from '@dvd/components/Section';
import TextLink from '@dvd/components/TextLink';
import Cta from '@dvd/modules/Cta';
import FullColumnText from '@dvd/modules/FullColumnText';
import G2SocialProof from '@dvd/modules/G2SocialProof';
import HeroSplit from '@dvd/modules/Heroes/HeroSplit';
import ImageCardColumns from '@dvd/modules/ImageCardColumns';
import Scrolling, { ScrollingChild } from '@dvd/modules/Scrolling';
import SplitText from '@dvd/modules/Splits/SplitText';
import TestimonialCard from '@dvd/components/TestimonialCard';
import HomepagePromo from '@content/HomepagePromo';
import Footer from '@content/navigation/Footer';
import { WISTIA_FREE_ACCOUNT } from '@lib/constants';
import Navigation from '@content/navigation/Navigation';
import PageTracker from '@lib/PageTracker';

const Page = () => {
  return (
    <>
      <PageTracker />
      <HomepagePromo />
      <Navigation />
      <main>
        <Section colorScheme="blue" gradient="fullSpectrum">
          <HeroSplit
            description="Video management, webinar hosting, and meaningful insights—together at last."
            video={{
              hashedId: 'i5voqxuarc',
              aspect: 1.7777777777777777,
              transcript:
                'You look stressed. I am stressed. Oh, and your laptop is on fire. It’s all these tabs. My video workflow is a mess. You should use Wistia. It’s one platform for all things video built specifically for business. This is nice with Wistia. You’ve got just one place to host, manage, and share your videos across your site and your marketing channels. One place to run polished on brand webinars that actually connect with your audience. One place to record and edit new videos. One place to track viewing data, capture leads and sync it all with your marketing tools. So literally all in one place. Literally all in one place. And with Wistia, one video can go a long way. Take any video, like a webinar recording or a product demo and turn it into short, shareable clips with AI powered tools. Post the clips to social or embed them on your site. No downloading. No re uploading. No switching tabs. I’m about to be everyone’s favorite colleague. And you’re no longer at risk of burning down the office. Also true. Wistia less work, more plays. That was close.',
            }}
            headline="One place for all your team’s video-ing"
            primaryCta={{
              href: '/pricing',
              text: 'See our plans',
              trackingLabel: 'see-our-plans',
              trackingProperty: 'homepage-hero',
            }}
            title="Video marketing platform"
            variant="landscape"
          />
        </Section>
        <Section colorScheme="light">
          <Scrolling
            description="We get it. That’s why we made Wistia—a video platform that helps you do it all, from “action” to analytics."
            headline="Tired of tool overload?"
          >
            <ScrollingChild
              cta={{
                href: '/product/hosting',
                text: 'Learn more',
                trackingLabel: 'host',
                trackingProperty: 'homepage-scrolling',
              }}
              description="Embed videos anywhere with our ad-free, customizable player. Plus, keep your video library organized with folders, filters, and tags."
              headline="Video hosting & management"
              image={{
                src: 'https://images.ctfassets.net/j7pfe8y48ry3/5iVYNdUbv4M4GbqrsAm6Mz/43da8ab0529fc6d32ea3e52528efbdd1/Host.png',
                alt: 'Woman in video player interface with customization and playlist options shown on a pink background.',
              }}
            />
            <ScrollingChild
              cta={{
                href: '/product/webinar-hosting',
                text: 'Learn more',
                trackingLabel: 'webinar-hosting',
                trackingProperty: 'homepage-scrolling',
              }}
              description="When you host a webinar with Wistia, you can customize the branding, connect to your marketing tools, and use your webinar recording as evergreen content."
              headline="Webinars"
              image={{
                src: 'https://images.ctfassets.net/j7pfe8y48ry3/7aliMDQn7f7idbgUZEwx0J/a8f097c3b5773b4265b540370cf1d55d/Webinar.png',
                alt: 'Man hosting a live webinar with participant thumbnails and chat interface on a yellow background.',
              }}
            />
            <ScrollingChild
              cta={{
                href: '/product/video-recording',
                text: 'Learn more',
                trackingLabel: 'record',
                trackingProperty: 'homepage-scrolling',
              }}
              description="No studio needed. Just grab your laptop to start recording. Then, use our AI editing tools to polish up your content."
              headline="Recording & editing"
              image={{
                src: 'https://images.ctfassets.net/j7pfe8y48ry3/3DGxPvchDjtZTvN2yi4sIC/d36ad358cfd7ce56a64121e70cee3cc6/Record.png',
                alt: 'Video software UI showing a video timeline and a video recorder and an image of a smiling man at a laptop recording a video.',
              }}
            />
            <ScrollingChild
              cta={{
                href: '/product/analytics',
                text: 'Learn more',
                trackingLabel: 'analyze',
                trackingProperty: 'homepage-scrolling',
              }}
              description="See exactly how viewers engage with your content, and instantly sync that data with your marketing tech stack."
              headline="Actionable insights"
              image={{
                src: 'https://images.ctfassets.net/buksmp4dkugj/6dyzue58uGKVcKljRrJ8YP/62ee9399323e4b706ba8ce60a4b8e394/home-analyzing.jpg',
                alt: 'Image of a smiling woman pointing at a desktop computer screen and video software UI showing an engagement chart and video total play performance',
              }}
            />
          </Scrolling>
          <FullColumnText topPadding={false}>
            <h2>Wistia syncs with your tech stack</h2>
            <p>
              Automate your workflows with video data when you connect Wistia to
              your favorite tools.
            </p>
            <TextLink
              cta={{
                href: '/integrations',
                text: 'Visit the Wistia integrations directory',
                trackingLabel: 'homepage-text-link',
                trackingProperty: 'integrations',
              }}
            />
          </FullColumnText>
          <LogoShowcase />
          <Cta
            headline="Like what you see?"
            primaryCta={{
              href: WISTIA_FREE_ACCOUNT,
              text: 'Sign up for free',
              trackingLabel: 'sign-up-for-free',
              trackingProperty: 'homepage-cta-middle',
            }}
            variant="pill"
          />
        </Section>
        <Section colorScheme="light">
          <ImageCardColumns
            description="Our team has you covered with expert advice to help you fine-tune your video strategy."
            headline="Read all about it"
          >
            <ImageCard
              alt="Promotional graphic reading “The State of Video Live Show” with a red live indicator dot, set against a dark curtain background. Event date shown as Wednesday, April 22."
              body="Ready to amp up your video strategy? Join us for data-backed insights, practical tips, and a whole lot of fun."
              colorScheme="yellow"
              cta={{
                href: '/explore/state-of-video-report?utm_source=storefront&utm_medium=homepage-block&utm_campaign=state-of-video-2026',
                text: 'Save a seat',
                trackingLabel: 'sov-live-show-2026',
                trackingProperty: 'homepage-card',
              }}
              heading="2026 State of Video Live Show"
              imgSrc="https://images.ctfassets.net/j7pfe8y48ry3/19SwLe5GXM2tRtZDtCwk4J/5cea81bf32cb679ca27956de0d3b8959/SOV-Live-Show-2026-Thumbnail.png"
            />
            <ImageCard
              alt="Blue gradient graphic with bold white text reading “AI video marketing trends,” and a pink label that says “for 2026,” surrounded by decorative, hand-drawn white arrows."
              body="See how teams like yours use AI to work smarter, make better videos, and engage audiences. Then, try it yourself."
              colorScheme="pink"
              cta={{
                href: '/explore/ai-video-trends',
                text: 'Read on',
                trackingLabel: 'ai-video-marketing-trends-report',
                trackingProperty: 'homepage-card',
              }}
              heading="AI Video Marketing Trends"
              imgSrc="https://images.ctfassets.net/j7pfe8y48ry3/6omhhc0Aa0qy7ff98QQX3L/72f2ad4ba9452442e3771c3030b99152/AI-Report-Thumbnail.png"
            />
            <ImageCard
              alt="Promotional graphic showing the text ‘Wistia vs Vimeo’ on a purple gradient background, alongside screenshots of Wistia heatmaps and a Wistia Channel named ‘Lenny’s Fan Club.’"
              body="See how they stack up on the stuff that really matters, like branding, analytics, integrations, and more. "
              colorScheme="blue"
              cta={{
                href: '/learn/marketing/wistia-vs-vimeo',
                text: 'Read on',
                trackingLabel: 'watch-wistia-vs-vimeo',
                trackingProperty: 'homepage-card',
              }}
              heading="Choosing between Wistia & Vimeo? "
              imgSrc="https://images.ctfassets.net/j7pfe8y48ry3/3qLrBtWtSxxjqmWdZPQ82n/362882429099852c36011e3715f910d4/Wistia-vs-Vimeo.png"
            />
          </ImageCardColumns>
          <SplitText
            description="Using Wistia to boost your video marketing is about as easy as it gets. But if you need a hand, our in-house experts are just an email or chat away."
            hashedId="353ysywt36"
            headline="Top-notch customer support"
            mediaOrientation="end"
          />
          <G2SocialProof
            heading="We think you’ll love Wistia, but don’t take our word for it."
            showAllBadges={false}
          >
            <TestimonialCard
              attribution="Gabriel Carrejo"
              attributionTitle="Head of Global Content Marketing at Automation Anywhere"
              backgroundColor="orange200"
              headline="Wistia has helped streamline video production."
              quote="Wistia is key for me to find clips, make notes and edits, and collaborate with vendors and internal teams."
              ratingColor="blue"
            />
            <TestimonialCard
              attribution="Scarlett Bush"
              attributionTitle="Director of Operations at Explainly"
              backgroundColor="yellow100"
              headline="Video management made easy!"
              quote="Thanks to Wistia, anyone on the team can easily upload, organize, and showcase our entire portfolio of videos in a sleek and searchable video gallery. The intuitive interface ensures quick access to all our content."
              ratingColor="blue"
            />
            <TestimonialCard
              attribution="M. Tre Gonzalez"
              attributionTitle="Director of Growth at OnCourse Systems for Education"
              backgroundColor="green300"
              headline="Elevated video marketing strategy made simple."
              quote="Wistia’s found the perfect balance for a video hosting platform. We use it for everything: lead capture for marketing videos, chaptered long-form webinars, internal documentation—the works."
              ratingColor="blue"
            />
            <TestimonialCard
              attribution="Jake Matulewicz"
              attributionTitle="Director of Marketing & Customer Experience at Symmons Industries"
              backgroundColor="blue200"
              headline="The best video marketing platform."
              quote="The analytics capabilities within the tool are really well built. I have used a couple of competitors in the past that weren’t nearly as streamlined and easy to use as Wistia!"
              ratingColor="blue"
            />
          </G2SocialProof>
          <Cta
            eyebrowText="Video marketing platform"
            headline="Ready to take Wistia for a spin?"
            primaryCta={{
              href: WISTIA_FREE_ACCOUNT,
              text: 'Sign up for free',
              trackingLabel: 'sign-up-for-free',
              trackingProperty: 'homepage-cta-footer',
            }}
            secondaryCta={{
              href: '/pricing/getintouch',
              text: 'Get in touch',
              trackingLabel: 'get-in-touch',
              trackingProperty: 'homepage-cta-footer',
            }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
};

export default Page;
